import type { WorldBackdrop } from './worldShapeKit';

/** Opening-only scenery. Open-bottom concave arches preserve their interior
 * negative space in the polygon renderer; no SVG holes, filters or new clocks.
 * Each plane has one shallow seam-matched footing and a few uneven landmarks. */
export const OPENING_AREA_BACKDROPS = {
  tomb: {
    far: {
      tile: {
        viewBoxWidth: 2400, viewBoxHeight: 480,
        path: 'M0 480V444H2400V480Z '
          // A broad barrel vault, a lower side chamber and a taller pointed bay.
          + 'M100 444V232C100 132 190 64 328 64C464 64 552 141 552 232V444 '
          + 'H518V232C518 164 438 105 328 105C221 105 134 166 134 232V444Z '
          + 'M760 444V262C760 185 854 123 980 123C1108 123 1200 184 1200 262V444 '
          + 'H1168V262C1168 207 1089 162 980 162C871 162 792 207 792 262V444Z '
          + 'M1450 444V228C1450 151 1570 80 1716 32C1862 80 1990 151 1990 228V444 '
          + 'H1948V232C1948 181 1842 123 1716 77C1594 124 1492 181 1492 232V444Z '
          // A narrow, partly collapsed end bay breaks the colonnade rhythm.
          + 'M2160 444V272L2194 272V212L2230 178L2270 156L2322 174L2350 221V444 '
          + 'H2320V258Q2311 208 2270 198Q2228 210 2224 258V444Z',
      },
    },
    mid: {
      tile: {
        viewBoxWidth: 2400, viewBoxHeight: 400,
        path: 'M0 400V378H198L220 371H520L548 378H1130L1154 367H1438L1468 378H2400V400Z '
          // Recessed chapels: thick lintels, uneven cornices and open entries.
          + 'M130 378V208H176V154H228V128H406V155H470V206H516V378H474V261 '
          + 'Q455 203 324 185Q196 204 172 261V378Z '
          + 'M882 378V194H932V168H1000V144H1174V170H1234V216H1290V378H1248V273 '
          + 'Q1120 214 924 274V378Z '
          + 'M1604 378V247H1650V191H1706V156H1848V178H1910V230H1950V378H1914V285 '
          + 'Q1810 218 1640 285V378Z '
          // One broken stair return stays below the larger vault silhouettes.
          + 'M2080 378V334H2122V299H2164V266H2204V235H2250V378Z',
      },
    },
    near: {
      tile: {
        viewBoxWidth: 2400, viewBoxHeight: 320,
        path: 'M0 320V296H290L312 290H508L530 296H1402L1426 291H1588L1610 296H2400V320Z '
          // Sparse side buttresses, never a continuous foreground wall.
          + 'M424 296V259H450V172L474 150V105H512V150L539 176V259H564V296Z '
          + 'M1780 296V263H1800V211L1825 178V127H1858V184L1888 215V265H1910V296Z',
      },
    },
  },
  hearth: {
    far: {
      tile: {
        viewBoxWidth: 2400, viewBoxHeight: 400,
        path: 'M0 400V312C174 308 212 253 420 256C629 259 694 320 872 319 '
          + 'C1062 318 1194 236 1390 246C1587 257 1654 310 1858 308 '
          + 'C2078 306 2220 295 2400 312V400Z '
          // A loose treeline, with clear valleys between unequal crown groups.
          + 'M268 295L274 245C236 254 215 230 224 207C205 181 229 152 258 158 '
          + 'C252 126 287 110 313 130C331 101 372 111 379 143C412 137 437 162 425 190 '
          + 'C451 213 425 246 389 239L367 260L374 290H355L353 257L333 241L291 255L286 295Z '
          + 'M1210 276L1218 224C1180 232 1158 207 1172 181C1152 150 1191 125 1214 139 '
          + 'C1230 102 1278 103 1288 137C1324 123 1351 150 1338 179 '
          + 'C1362 201 1336 227 1298 225L1280 242L1285 272H1263L1262 235L1234 231L1230 276Z '
          + 'M2014 309L2018 263C1986 269 1960 246 1974 223C1958 199 1980 177 2006 180 '
          + 'C2016 148 2055 143 2074 169C2104 152 2139 172 2135 201 '
          + 'C2166 215 2150 249 2118 252L2076 253L2050 274L2057 308H2036L2032 264L2026 270L2028 309Z',
      },
    },
    mid: {
      tile: {
        viewBoxWidth: 2400, viewBoxHeight: 320,
        path: 'M0 320V264H140V230L220 160L250 187V138H271V203L303 230V268 '
          + 'H370V204L448 128L530 196V268H600V236L638 210L706 244V270 '
          + 'C826 266 900 275 1050 266L1050 214L1138 154L1180 182V128H1202V197 '
          + 'L1234 217V258H1280V232L1330 178L1392 226V266 '
          + 'C1510 265 1628 252 1740 269L1782 269V222L1842 174L1910 218V268 '
          + 'H1996V210L2080 135L2100 153V116H2122V173L2154 203V266 '
          + 'C2240 274 2322 260 2400 264V320Z',
      },
    },
    near: {
      tile: {
        viewBoxWidth: 2400, viewBoxHeight: 240,
        path: 'M0 240V218C171 215 235 225 420 220C619 214 718 226 920 220 '
          + 'C1106 214 1260 224 1450 221C1693 214 1800 226 2030 220C2190 216 2300 215 2400 218V240Z '
          // Low orchard crowns and a few roof/fence posts leave the square open.
          + 'M278 220L282 182C254 188 235 172 244 153C231 132 250 108 276 114 '
          + 'C288 89 320 89 333 111C359 102 383 122 376 144C397 168 369 189 342 181 '
          + 'L300 185L300 220Z '
          + 'M1000 223V170H1016V193H1090V166H1106V223H1090V206H1016V223Z '
          + 'M1772 221V176H1790V163H1804V176H1822V221H1808V194H1786V221Z',
      },
    },
  },
} satisfies Record<string, WorldBackdrop>;
